import { Component } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import _ from "lodash";
import { Feature, FeatureChoice } from "app/model/entities/entity-model";
import { ActiveStatusEnum } from "app/model/entities/enums";
import { DialogBaseComponent, DialogParams } from "@/shared/dialog-base.component";
import { DomainService } from "@/shared/domain.service";

export interface AddFeedColorsDialogParams extends DialogParams {
  /** Names the manufacturer publishes that the Color feature does not offer. */
  colorNames: string[];
  styleCode: string;
  manufacturerName: string;
}

interface ColorRow {
  name: string;
  isChecked: boolean;
}

/** The Color feature is identified by name, not by id, so a renamed or missing one is caught. */
export const COLOR_FEATURE_NAME = "Color";

/**
 * Adds colors the manufacturer publishes to the Uniformax Color list.
 *
 * Only creates the choices. It deliberately does not build products in them — whether a color
 * is worth stocking is a purchasing decision, and the product generator is where that is made.
 */
@Component({
  templateUrl: "./add-feed-colors-dialog.component.html",
  standalone: false,
})
export class AddFeedColorsDialogComponent extends DialogBaseComponent<AddFeedColorsDialogParams> {
  rows: ColorRow[] = [];
  colorFeature: Feature | null = null;
  isSaving = false;
  errorMessage = "";

  constructor(
    public config: DynamicDialogConfig<AddFeedColorsDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, { header: "Add Colors to the Color List", width: "620px", height: "620px" });
    this.prepare();
  }

  private async prepare() {
    const features = await this.dbQueryService.getFeatures(true);
    this.colorFeature =
      features.find((f) => (f.name ?? "").trim().toLowerCase() === COLOR_FEATURE_NAME.toLowerCase()) ?? null;

    if (this.colorFeature == null) {
      this.errorMessage =
        `There is no feature named '${COLOR_FEATURE_NAME}'. The system cannot add colors until that feature exists.`;
    } else {
      this.rows = this.params.colorNames.map((name) => ({ name, isChecked: false }));
    }
    this.isPageReady = true;
  }

  get checkedCount() {
    return this.rows.filter((r) => r.isChecked).length;
  }

  get canSave() {
    return this.colorFeature != null && this.checkedCount > 0 && !this.isSaving;
  }

  checkAll(isChecked: boolean) {
    this.rows.forEach((r) => (r.isChecked = isChecked));
  }

  async onOk() {
    if (!this.canSave || this.colorFeature == null) return;
    const feature = this.colorFeature;
    const chosen = this.rows.filter((r) => r.isChecked).map((r) => r.name);

    const ok = await this.dialogService.yesNoDialog(
      "Add Colors",
      `The system adds ${chosen.length} color(s) to the '${feature.name}' list.`
        + ` They become available to every product type that uses the feature, not only ${this.params.styleCode}.`
        + ` Do you want to continue?`,
      "Ok",
      "Cancel"
    );
    if (!ok) return;

    this.isSaving = true;
    this.errorMessage = "";
    try {
      // Names already on the feature are skipped rather than duplicated. FeatureChoice has a
      // unique index on (FeatureId, ChoiceValue), so a duplicate would fail the whole save.
      const existing = new Set(feature.featureChoices.map((fc) => (fc.choiceValue ?? "").trim().toLowerCase()));
      let displayOrder = (_.max(feature.featureChoices.map((fc) => fc.displayOrder ?? 0)) ?? 0) + 1;

      chosen.forEach((name) => {
        const clean = name.trim();
        if (clean.length === 0 || existing.has(clean.toLowerCase())) return;
        this.dbSaveService.createEntity(FeatureChoice, {
          featureId: feature.id,
          choiceValue: clean,
          displayOrder: displayOrder++,
          activeStatusId: ActiveStatusEnum.Active,
        });
        existing.add(clean.toLowerCase());
      });

      const saved = await this.dbSaveService.saveChanges();
      if (!saved) {
        this.isSaving = false;
        return;
      }
      this.dialogRef.close({ added: chosen.length });
    } catch (e: any) {
      this.uow.rollback();
      this.errorMessage = `The system could not add the colors. ${e?.message ?? ""}`;
      this.isSaving = false;
    }
  }

  onCancel() {
    this.uow.rollback();
    this.dialogRef.close(null);
  }
}
